import { useEffect, useState } from 'react';
import { fetchPageViewStats, type PageViewStats } from '../../lib/pageViewTracking';

const nf = new Intl.NumberFormat('fr-CA');
const df = new Intl.DateTimeFormat('fr-CA', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const startFmt = new Intl.DateTimeFormat('fr-CA', { dateStyle: 'long', timeStyle: 'short', timeZone: 'America/Toronto' });

function delta(cur: number, prev: number): string {
  if (prev === 0) return cur === 0 ? '—' : 'nouveau';
  const pct = Math.round(((cur - prev) / prev) * 100);
  return `${pct > 0 ? '+' : ''}${pct} %`;
}

interface Props {
  userId: number;
  leagueId: number;
  /** 7, 30, 90 ou null pour la saison. */
  days: number | null;
}

export function PageViewActivityPanel({ userId, leagueId, days }: Props) {
  const [stats, setStats] = useState<PageViewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchPageViewStats(userId, leagueId, days).then(({ data, error: err }) => {
      if (cancelled) return;
      setStats(err ? null : data);
      setError(!!err);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [userId, leagueId, days]);

  if (loading) return <section className="ci-section">Chargement des pages consultées…</section>;
  if (error || !stats) return <div className="ci-error">Statistiques des pages consultées indisponibles.</div>;

  const avg = stats.unique_users > 0 ? stats.total_views / stats.unique_users : 0;
  const prevAvg = stats.previous_unique_users > 0 ? stats.previous_total_views / stats.previous_unique_users : 0;

  return (
    <section className="ci-section">
      <div className="ci-section__header">
        <div>
          <h2 className="ci-section__title">Consultations des pages</h2>
          <p className="ci-section__meta">
            {stats.tracking_start
              ? `Suivi depuis le ${startFmt.format(new Date(stats.tracking_start))} (aucune donnée avant).`
              : 'Aucune visite enregistrée pour le moment.'}
          </p>
        </div>
      </div>

      <div className="ci-kpis ci-kpis--metrics">
        <div className="ci-kpi-card"><div className="ci-kpi-card__content">
          <h3>Poolers ayant consulté</h3>
          <p className="ci-kpi-card__value">{nf.format(stats.unique_users)}</p>
          {stats.previous_complete && <p className="ci-kpi-card__label">{delta(stats.unique_users, stats.previous_unique_users)} vs période précédente</p>}
        </div></div>
        <div className="ci-kpi-card"><div className="ci-kpi-card__content">
          <h3>Pages consultées</h3>
          <p className="ci-kpi-card__value">{nf.format(stats.total_views)}</p>
          {stats.previous_complete && <p className="ci-kpi-card__label">{delta(stats.total_views, stats.previous_total_views)} vs période précédente</p>}
        </div></div>
        <div className="ci-kpi-card"><div className="ci-kpi-card__content">
          <h3>Pages par pooler</h3>
          <p className="ci-kpi-card__value">{avg.toFixed(1)}</p>
          {stats.previous_complete && <p className="ci-kpi-card__label">{delta(avg, prevAvg)} vs période précédente</p>}
        </div></div>
      </div>

      {stats.daily.length > 0 && (
        <div className="ci-daily-stats">
          <h3>Consultations par jour</h3>
          <table className="ci-table ci-table--compact">
            <thead><tr><th>Date</th><th className="num">Poolers</th><th className="num">Pages</th></tr></thead>
            <tbody>
              {[...stats.daily].reverse().slice(0, 14).map((d) => (
                <tr key={d.day}>
                  <td>{df.format(new Date(`${d.day}T00:00:00Z`))}</td>
                  <td className="num">{nf.format(d.unique_users)}</td>
                  <td className="num">{nf.format(d.views)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {stats.top_pages.length > 0 && (
        <div className="ci-ranked-pages">
          <h3>Pages les plus populaires</h3>
          <ol className="ci-ranked-pages__list">
            {stats.top_pages.map((p, i) => (
              <li key={p.page_path}>
                <span className="ci-ranked-pages__rank">{i + 1}</span>
                <span className="ci-ranked-pages__path">{p.page_path}</span>
                <span className="ci-ranked-pages__views">{nf.format(p.views)} vues · {nf.format(p.unique_users)} poolers</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </section>
  );
}
